import { useMemo, useState } from "react";
import type { Task, TaskStatus } from "@shared/types";

// Dependency flow diagram: a swimlane view with one lane per person (Who does
// it), tasks placed left→right by `sequence`, and orthogonal connectors drawn
// between dependent tasks. Edges come from the `Receives from` cell
// (tasks.receivesFrom), which references upstream task codes (tasks.sourceRef),
// e.g. "1.1.2" or "1.1.1 through 1.1.11". Free-text origins (e.g. "Field,
// originates…") contain no task code and are ignored.

const STATUS_DOT: Record<TaskStatus, string> = {
  todo: "bg-ink-400",
  in_progress: "bg-accent",
  review: "bg-warn",
  done: "bg-positive",
};

// Layout constants (px).
const LANE_LABEL_W = 132;
const CARD_W = 184;
const CARD_H = 70;
const GAP_X = 56;
const GAP_Y = 22;
const TOP_PAD = 8;

// Pull task codes like 1, 1.1, 1.1.11 out of a free-text "Receives from" cell.
function parseCodes(text: string | null): string[] {
  if (!text) return [];
  const matches = text.match(/\d+(?:\.\d+)+/g) ?? [];
  return [...new Set(matches)];
}

export default function WorkflowFlow({ tasks }: { tasks: Task[] }) {
  const stages = useMemo(() => {
    const set = new Set<string>();
    for (const t of tasks) set.add(t.workflowStage?.trim() || "Unstaged");
    // Order stages by the lowest sequence within each.
    const order = new Map<string, number>();
    for (const t of tasks) {
      const s = t.workflowStage?.trim() || "Unstaged";
      const seq = t.sequence ?? Number.MAX_SAFE_INTEGER;
      order.set(s, Math.min(order.get(s) ?? Number.MAX_SAFE_INTEGER, seq));
    }
    return [...set].sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0) || a.localeCompare(b));
  }, [tasks]);

  const [stage, setStage] = useState<string>(stages[0] ?? "");
  const activeStage = stages.includes(stage) ? stage : stages[0] ?? "";

  const stageTasks = useMemo(
    () => tasks.filter((t) => (t.workflowStage?.trim() || "Unstaged") === activeStage),
    [tasks, activeStage]
  );

  const layout = useMemo(() => {
    // Lanes = people, ordered by their earliest sequence.
    const laneOrder = new Map<string, number>();
    for (const t of stageTasks) {
      const lane = t.assigneeLabel?.trim() || "Unassigned";
      const seq = t.sequence ?? Number.MAX_SAFE_INTEGER;
      laneOrder.set(lane, Math.min(laneOrder.get(lane) ?? Number.MAX_SAFE_INTEGER, seq));
    }
    const lanes = [...laneOrder.keys()].sort((a, b) => (laneOrder.get(a) ?? 0) - (laneOrder.get(b) ?? 0) || a.localeCompare(b));
    const laneRow = new Map(lanes.map((l, i) => [l, i]));

    // Columns = distinct sequence values (tasks without a sequence go last).
    const seqs = [...new Set(stageTasks.map((t) => t.sequence ?? Number.MAX_SAFE_INTEGER))].sort((a, b) => a - b);
    const seqCol = new Map(seqs.map((s, i) => [s, i]));

    const placed = stageTasks.map((t) => {
      const lane = t.assigneeLabel?.trim() || "Unassigned";
      const row = laneRow.get(lane) ?? 0;
      const col = seqCol.get(t.sequence ?? Number.MAX_SAFE_INTEGER) ?? 0;
      const x = LANE_LABEL_W + col * (CARD_W + GAP_X);
      const y = TOP_PAD + row * (CARD_H + GAP_Y);
      return { task: t, x, y };
    });

    // Index by task code for edge resolution.
    const byCode = new Map<string, (typeof placed)[number]>();
    for (const p of placed) if (p.task.sourceRef) byCode.set(p.task.sourceRef, p);

    const edges: { x1: number; y1: number; x2: number; y2: number }[] = [];
    for (const p of placed) {
      for (const code of parseCodes(p.task.receivesFrom)) {
        const src = byCode.get(code);
        if (!src || src === p) continue; // only connect codes visible in this stage
        edges.push({
          x1: src.x + CARD_W,
          y1: src.y + CARD_H / 2,
          x2: p.x,
          y2: p.y + CARD_H / 2,
        });
      }
    }

    const width = LANE_LABEL_W + Math.max(1, seqs.length) * (CARD_W + GAP_X);
    const height = TOP_PAD + Math.max(1, lanes.length) * (CARD_H + GAP_Y);
    return { lanes, laneRow, placed, edges, width, height };
  }, [stageTasks]);

  if (stages.length === 0) {
    return <p className="py-8 text-center text-sm text-ink-400">No workflow tasks to chart.</p>;
  }

  return (
    <div>
      {/* Stage tabs */}
      <div className="mb-3 flex flex-wrap gap-1.5">
        {stages.map((s) => (
          <button
            key={s}
            onClick={() => setStage(s)}
            className={`rounded-lg px-3 py-1 text-sm ${
              s === activeStage ? "bg-accent text-white" : "border border-ink-200 bg-white text-ink-600 hover:bg-ink-50"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <p className="mb-2 text-xs text-ink-400">
        Lanes are people; cards flow left→right by sequence. Arrows show “receives from” dependencies between tasks in
        this stage.
      </p>

      <div className="overflow-auto rounded-lg border border-ink-200 bg-ink-50/40 p-2">
        <div className="relative" style={{ width: layout.width, height: layout.height }}>
          {/* Lane backgrounds + labels */}
          {layout.lanes.map((lane, i) => (
            <div
              key={lane}
              className="absolute left-0 flex items-center"
              style={{ top: TOP_PAD + i * (CARD_H + GAP_Y), height: CARD_H, width: layout.width }}
            >
              <div className="absolute inset-x-0 h-full rounded-lg bg-white/60" />
              <div className="relative z-10 w-[120px] truncate pr-2 text-xs font-semibold text-ink-600" title={lane}>
                {lane}
              </div>
            </div>
          ))}

          {/* Connectors */}
          <svg className="pointer-events-none absolute inset-0" width={layout.width} height={layout.height}>
            <defs>
              <marker id="wf-arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                <path d="M0,0 L6,3 L0,6 Z" fill="#94A3B8" />
              </marker>
            </defs>
            {layout.edges.map((e, i) => {
              const midX = e.x2 >= e.x1 ? (e.x1 + e.x2) / 2 : e.x1 + GAP_X / 2;
              return (
                <path
                  key={i}
                  d={`M ${e.x1} ${e.y1} H ${midX} V ${e.y2} H ${e.x2}`}
                  fill="none"
                  stroke="#94A3B8"
                  strokeWidth={1.5}
                  markerEnd="url(#wf-arrow)"
                />
              );
            })}
          </svg>

          {/* Task cards */}
          {layout.placed.map(({ task, x, y }) => (
            <div
              key={task.id}
              className="absolute z-10 flex flex-col justify-between overflow-hidden rounded-lg border border-ink-200 bg-white p-2 shadow-card"
              style={{ left: x, top: y, width: CARD_W, height: CARD_H }}
              title={task.title}
            >
              <div className="flex items-start gap-1.5">
                <span className={`mt-0.5 h-2 w-2 shrink-0 rounded-full ${STATUS_DOT[task.status]}`} />
                <span className="line-clamp-2 text-xs font-medium leading-snug text-ink-800">{task.title}</span>
              </div>
              <div className="flex items-center justify-between text-[10px] text-ink-400">
                <span>{task.sourceRef ?? ""}</span>
                {task.recurrence !== "once" && (
                  <span className="rounded bg-accent-50 px-1 font-medium capitalize text-accent-700">{task.recurrence}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
